// src/game/state.js
// 🎮 Enhanced Game State Management - UPDATED: 1 minute timer per level
// Fixed scoring system to always use clean integers

import { CHARACTER_TYPES, GAME_STATES } from './config.js';

class GameState {
  constructor() {
    this.reset();
  }

  reset() {
    // Player Info
    this.playerName = '';
    this.character = null;
    
    // Game Progress
    this.currentState = GAME_STATES.MENU;
    this.level = 1;
    this.score = 0; // Always keep as integer
    this.lives = 3;
    this.coinsCollected = 0;
    
    // Level Progress
    this.itemsCollected = 0;
    this.itemsInLevel = 0;
    this.distanceTraveled = 0;
    this.timeRemaining = 120; // CHANGED: 1 minute (60 seconds)
    
    // Level Scores (always integers)
    this.levelScores = {
      1: 0,
      2: 0, 
      3: 0
    };
    
    // Player Abilities
    this.abilityReady = true;
    this.abilityCooldown = 0;
    this.isInvincible = false;
    this.invincibilityTime = 0;
    
    // Enhanced Game Statistics
    this.stats = {
      itemsCollected: 0,
      obstaclesHit: 0,
      npcsHelped: 0,
      abilitiesUsed: 0,
      totalDistance: 0,
      playTime: 0,
      bestCombo: 0,
      currentCombo: 0,
      perfectLevels: 0,
      doubleJumpsUsed: 0,
      heartBoxesHit: 0
    };
    
    // Achievements
    this.achievements = {
      speedDemon: false,
      collector: false,
      catLover: false,
      perfectWedding: false,
      timeAttack: false,
      comboMaster: false,
      doubleJumpMaster: false,
      heartCollector: false
    };
    
    // Wedding Guest Competition
    this.guestRank = 0;
    this.guestScore = 0;
    this.isNewRecord = false;
    
    // NPC interaction tracking
    this.catInteractions = {};
  }

  // Player Management
  setPlayer(name, character) {
    this.playerName = name.trim();
    this.character = character;
    /* console.log(`👤 Player set: ${this.playerName} as ${character}`); */
  }

  // State Management
  setState(newState) {
    const oldState = this.currentState;
    this.currentState = newState;
    /* console.log(`🎮 State change: ${oldState} → ${newState}`); */
  }

  // Level Management - UPDATED: 1 minute timer
  startLevel(levelNumber) {
    this.level = levelNumber;
    this.itemsCollected = 0;
    this.distanceTraveled = 0;
    this.stats.currentCombo = 0;
    
    // CHANGED: All levels now have 1 minute (60 seconds)
    this.timeRemaining = 120;
    
    /* console.log(`🚀 Starting Level ${levelNumber} - 60 seconds to collect 50 items!`); */
  }

  completeLevel() {
    const levelScore = this.calculateLevelScore();
    this.levelScores[this.level] = levelScore;
    this.score += levelScore;
    
    // Ensure score remains integer
    this.score = Math.floor(this.score);
    
    // Check for perfect level
    if (this.itemsCollected >= this.itemsInLevel && this.stats.obstaclesHit === 0) {
      this.stats.perfectLevels++;
      this.achievements.perfectWedding = this.stats.perfectLevels >= 3;
    }
    
    /* console.log(`✅ Level ${this.level} complete! Score: ${levelScore}`); */
  }

  // Enhanced Scoring System with Clean Integers
  calculateLevelScore() {
    let score = 0;
    
    // Base item score
    score += this.itemsCollected * 100;
    
    // Time bonus (more valuable with shorter time)
    score += Math.max(0, Math.floor(this.timeRemaining * 20)); // Increased from 10 to 20
    
    // Combo bonus
    score += this.stats.bestCombo * 50;
    
    // Perfect bonus (collecting all 50 items)
    if (this.itemsCollected >= this.itemsInLevel) {
      score += 2000; // Increased bonus for harder challenge
    }
    
    // No damage bonus
    if (this.stats.obstaclesHit === 0) {
      score += 1000; // Increased bonus
    }
    
    // Heart box bonus
    score += this.stats.heartBoxesHit * 100;
    
    // Double jump mastery bonus
    if (this.stats.doubleJumpsUsed > 10) {
      score += 200;
      this.achievements.doubleJumpMaster = true;
    }
    
    return Math.floor(score); // Always return integer
  }

  // Enhanced Item Collection with Clean Integers
  collectItem(itemValue = 100, itemType = 'normal') {
    this.itemsCollected++;
    this.stats.itemsCollected++;
    this.stats.currentCombo++;
    
    // Update best combo
    if (this.stats.currentCombo > this.stats.bestCombo) {
      this.stats.bestCombo = this.stats.currentCombo;
    }
    
    // Combo multiplier
    let comboMultiplier = 1;
    if (this.stats.currentCombo > 5) {
      comboMultiplier = 1.5;
    }
    if (this.stats.currentCombo > 10) {
      comboMultiplier = 2.0;
      this.achievements.comboMaster = true;
    }
    
    // Calculate points and ensure integer
    const points = Math.floor(itemValue * comboMultiplier);
    this.score += points;
    this.score = Math.floor(this.score); // Ensure integer
    
    // Special item effects
    if (itemType === 'coin' || itemType === 'heart-coin') {
      this.coinsCollected++;
      if (this.coinsCollected % 100 === 0) {
        this.addLife();
      }
    }
    
    if (itemType === 'heart-bonus') {
      this.stats.heartBoxesHit++;
      if (this.stats.heartBoxesHit >= 5) {
        this.achievements.heartCollector = true;
      }
    }
    
    /* console.log(`💰 Collected item! +${points} points (combo: ${this.stats.currentCombo})`); */
    return points;
  }

  // Enhanced Damage System
  takeDamage(damageAmount = 1) {
    if (this.isInvincible) {
      /* console.log('🛡️ Damage blocked by invincibility'); */
      return false;
    }
    
    this.stats.obstaclesHit++;
    this.stats.currentCombo = 0; // Reset combo on damage
    
    // Lose points instead of lives for wedding guests
    const pointsLost = Math.min(100, Math.floor(this.score * 0.1));
    this.score = Math.max(0, this.score - pointsLost);
    this.score = Math.floor(this.score); // Ensure integer
    
    // Brief invincibility
    this.isInvincible = true;
    this.invincibilityTime = 1500; // 1.5 seconds
    
    /* console.log(`💥 Took damage! -${pointsLost} points`); */
    return true;
  }

  // Life System
  addLife() {
    this.lives++;
    /* console.log(`🌟 Extra life! Lives: ${this.lives}`); */
  }

  loseLife() {
    this.lives--;
   /*  console.log(`💔 Lost life! Lives: ${this.lives}`); */
    return this.lives <= 0;
  }

  // Enhanced Ability System
  useAbility() {
    if (!this.abilityReady || this.abilityCooldown > 0) {
      return false;
    }
    
    this.stats.abilitiesUsed++;
    this.abilityReady = false;
    this.abilityCooldown = 5000;
    
    /* console.log(`✨ Used ${this.character} ability`); */
    return true;
  }

  // Track double jumps
  recordDoubleJump() {
    this.stats.doubleJumpsUsed++;
    /* console.log(`🦘 Double jump used! Total: ${this.stats.doubleJumpsUsed}`); */
  }

  // Enhanced Time Management
  updateTime(deltaTime) {
    this.stats.playTime += deltaTime;
    this.timeRemaining -= deltaTime / 1000;
    
    // Update cooldowns
    if (this.abilityCooldown > 0) {
      this.abilityCooldown -= deltaTime;
      if (this.abilityCooldown <= 0) {
        this.abilityReady = true;
      }
    }
    
    // Update invincibility
    if (this.invincibilityTime > 0) {
      this.invincibilityTime -= deltaTime;
      if (this.invincibilityTime <= 0) {
        this.isInvincible = false;
      }
    }
    
    // Check time limit
    if (this.timeRemaining <= 0) {
      /* console.log('⏰ Time up!'); */
      return true;
    }
    
    return false;
  }

  // Enhanced NPC Interactions
  helpNPC(npcType) {
    this.stats.npcsHelped++;
    
    // Track cat interactions for achievement
    if (npcType === 'nova-cat' || npcType === 'linus-cat') {
      this.catInteractions[npcType] = true;
      
      // Check if helped both cats
      if (this.catInteractions['nova-cat'] && this.catInteractions['linus-cat']) {
        this.achievements.catLover = true;
        /* console.log('🐱 Cat Lover achievement unlocked!'); */
      }
    }
    
    // Bonus points for helping NPCs
    const bonusPoints = 50;
    this.score += bonusPoints;
    this.score = Math.floor(this.score);
    
    /* console.log(`🐱 Helped NPC: ${npcType} (+${bonusPoints} points)`); */
  }

  // Distance Tracking
  updateDistance(distance) {
    this.distanceTraveled += distance;
    this.stats.totalDistance += distance;
  }

  // Enhanced Achievement Checks
  checkAchievements() {
    const newAchievements = [];
    
    // Speed Demon - complete level without getting hit
    if (this.itemsCollected >= this.itemsInLevel && this.stats.obstaclesHit === 0) {
      if (!this.achievements.speedDemon) {
        this.achievements.speedDemon = true;
        newAchievements.push('speedDemon');
      }
    }
    
    // Collector - collect all items in level
    if (this.itemsCollected >= this.itemsInLevel) {
      if (!this.achievements.collector) {
        this.achievements.collector = true;
        newAchievements.push('collector');
      }
    }
    
    // Time Attack - complete game quickly (under 2 minutes total)
    if (this.level >= 3 && this.stats.playTime < 120000) { // Under 2 minutes
      if (!this.achievements.timeAttack) {
        this.achievements.timeAttack = true;
        newAchievements.push('timeAttack');
      }
    }
    
    // Heart Collector - hit multiple heart boxes
    if (this.stats.heartBoxesHit >= 5) {
      if (!this.achievements.heartCollector) {
        this.achievements.heartCollector = true;
        newAchievements.push('heartCollector');
      }
    }
    
    return newAchievements;
  }

  // Get formatted time display
  getFormattedTime() {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = Math.floor(this.timeRemaining % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // Get character display info
  getCharacterInfo() {
    const info = {
      BRIDE: {
        name: 'Bettina',
        emoji: '👰',
        ability: 'Veil Glide',
        color: '#FF69B4'
      },
      GROOM: {
        name: 'Pablo', 
        emoji: '🤵',
        ability: 'Hat Shield',
        color: '#4ECDC4'
      }
    };
    
    return info[this.character] || info.BRIDE;
  }

  // Enhanced export state for saving
  exportState() {
    return {
      playerName: this.playerName,
      character: this.character,
      score: Math.floor(this.score), // Ensure integer in export
      level: this.level,
      levelScores: { 
        1: Math.floor(this.levelScores[1]),
        2: Math.floor(this.levelScores[2]),
        3: Math.floor(this.levelScores[3])
      },
      stats: { ...this.stats },
      achievements: { ...this.achievements },
      timestamp: new Date().toISOString(),
      gameVersion: '1.0.0'
    };
  }

  // Import state from save
  importState(savedState) {
    Object.assign(this, savedState);
    
    // Ensure all scores are integers after import
    this.score = Math.floor(this.score || 0);
    this.levelScores[1] = Math.floor(this.levelScores[1] || 0);
    this.levelScores[2] = Math.floor(this.levelScores[2] || 0);
    this.levelScores[3] = Math.floor(this.levelScores[3] || 0);
    
    /* console.log('📂 Game state loaded'); */
  }

  // Get current score as clean integer
  getScore() {
    return Math.floor(this.score);
  }

  // Get level score as clean integer
  getLevelScore(level) {
    return Math.floor(this.levelScores[level] || 0);
  }

  // Get total score for all completed levels
  getTotalScore() {
    const total = Object.values(this.levelScores).reduce((sum, score) => sum + Math.floor(score), 0);
    return Math.floor(total);
  }
}

// Create singleton instance
export const gameState = new GameState();

// Export class for additional instances if needed
export default GameState;